import {
  Component,
  Inject,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  MbHorizontalMenuComponent,
  MbButtonComponent,
  MbModalComponent,
  MbEmptyListComponent,
  MbBannerComponent,
  ToastService,
  MbSearchComponent,
  MbSkeletonComponent,
  MbSelectComponent,
} from '../../../components/common';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
// NOTE: FormBuilder, FormGroup, and Validators are kept in the import above because
// they are still used by the (currently disabled) closeComplaintForm. If the
// Dismiss/Close Complaint workflow is removed permanently, these three can be
// dropped from this import.
import { User } from '../../../store/auth/auth.state';
import { selectAuthUser } from '../../../store/auth/auth.selectors';
import { Store } from '@ngrx/store';
import { ASSET_CONSTANTS } from '../../../shared/constants/asset-constants';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NewCaseFormComponent } from '../../../components/forms';
import {
  ComplaintsService,
  IComplaintDetail,
} from '../../../core/services/complaints.services';
import {
  IContent,
  LocationsService,
} from '../../../core/services/locations.services';
import { EComplaintStatus } from '../../../utils/enums';
import {
  CasesService,
  ICaseContent,
} from '../../../core/services/cases.services';
import { Router } from '@angular/router';
import { NzPaginationComponent } from 'ng-zorro-antd/pagination';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { HasPermissionDirective } from '../../../components/common/mb-permission-directive/mb-permission-directive.component';
import { forkJoin } from 'rxjs';
import { Subject, takeUntil } from 'rxjs';
import { DaysAgoPipe } from '../../../shared/pipes/days-ago.pipe';

interface ILocationFilterForm {
  district: string;
  sector: string;
  cell: string;
  village: string;
}

interface IComplaintFilterForm extends ILocationFilterForm {
  scope: string;
  isAcknowledged: '' | 'true' | 'false';
}

interface IUrgentCasesViewState {
  appliedFilterForm: ILocationFilterForm;
  currentPage: number;
  pageSize: number;
}

type TrackingLevel =
  | 'NATIONAL'
  | 'PROVINCE'
  | 'DISTRICT'
  | 'SECTOR'
  | 'CELL'
  | 'VILLAGE';

type ScopeFilter = 'TODAY' | 'THIS_WEEK' | 'ALL_TIME' | '';

interface IComplaintListItem {
  id: string;
  title: string;
  status: EComplaintStatus;
  description: string;
  createdAt: string;
  createdFor: string;
}

interface IFilterOption {
  labelKey: string;
  scope: ScopeFilter | null;
}

const FILTER_OPTIONS: IFilterOption[] = [
  { labelKey: 'today', scope: 'TODAY' },
  { labelKey: 'thisWeek', scope: 'THIS_WEEK' },
  { labelKey: 'rejectedComplaints', scope: null },
  { labelKey: 'allTime', scope: 'ALL_TIME' },
];

@Component({
  selector: 'app-complaints-overview-page',
  imports: [
    MbHorizontalMenuComponent,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MbButtonComponent,
    MbModalComponent,
    MbEmptyListComponent,
    NzTableModule,
    MbBannerComponent,
    NewCaseFormComponent,
    NzPaginationComponent,
    TranslocoDirective,
    HasPermissionDirective,
    MbSearchComponent,
    MbSkeletonComponent,
    MbSelectComponent,
    DaysAgoPipe,
  ],
  templateUrl: './complaints-overview-page.component.html',
})
export class ComplaintsOverviewPageComponent implements OnInit, OnDestroy {
  @ViewChild('newCaseForm') newCaseForm!: NewCaseFormComponent;

  get isCaseSubmitting(): boolean {
    return this.newCaseForm?.isSubmitting ?? false;
  }
  user: User | null = null;
  userTrackingLevel: TrackingLevel | '' = '';
  userAssignedLocations: User['assignedLocations'] = [];
  isModalVisible = false;
  selectedComplaintId: string | null = null;
  selectedComplaint: IComplaintDetail | null = null;
  isLoading = false;
  isLoadingComplaints = false;
  isLoadingUrgentCases = false;
  complaintsError: string | null = null;
  listOfUrgentCases: ICaseContent[] = [];
  selectedFilter = '';
  isAcknowledgedFilter: boolean | null = false;
  filteredComplaints: IComplaintListItem[] = [];
  isFilterModalVisible = false;
  isUrgentFilterModalVisible = false;

  // ─── Close / Reject Complaint state - DISABLED ────────────────────────────
  // The Dismiss Complaint button on the New Case modal was removed, so this
  // state and its associated form are no longer reachable. Kept commented for
  // future reference / quick re-enabling.
  // isCloseComplaintModalVisible = false;
  // isClosingComplaint = false;
  // closeComplaintForm: FormGroup;

  searchTerm = '';
  complaintFilterOptions: Record<string, string> = {};
  urgentFilterOptions: Record<string, string> = {};
  complaintFilterForm: IComplaintFilterForm = this.createDefaultFilterForm();
  appliedComplaintFilterForm: IComplaintFilterForm =
    this.createDefaultFilterForm();
  urgentFilterForm: ILocationFilterForm =
    this.createDefaultLocationFilterForm();
  appliedUrgentFilterForm: ILocationFilterForm =
    this.createDefaultLocationFilterForm();
  districtOptions: { value: string; label: string }[] = [];
  sectorOptions: { value: string; label: string }[] = [];
  cellOptions: { value: string; label: string }[] = [];
  villageOptions: { value: string; label: string }[] = [];
  urgentDistrictOptions: { value: string; label: string }[] = [];
  urgentSectorOptions: { value: string; label: string }[] = [];
  urgentCellOptions: { value: string; label: string }[] = [];
  urgentVillageOptions: { value: string; label: string }[] = [];
  // Complaint pagination properties
  currentPage = 1;
  pageSize = 6;
  totalItems = 0;

  // Urgent cases pagination properties
  urgentCurrentPage = 1;
  urgentPageSize = 6;
  urgentTotalItems = 0;

  readonly filterOptions = FILTER_OPTIONS;
  protected readonly ASSET_CONSTANTS = ASSET_CONSTANTS;

  private readonly searchDebounceMs = 500;
  private readonly urgentCasesViewStateKey =
    'complaints-overview.urgent-cases-view-state';
  private searchDebounceId: ReturnType<typeof setTimeout> | null = null;
  private locationContextKey = '';
  private hasAppliedUrgentLocationFilter = false;
  private complaintsApiTotalPages = 0;
  private urgentApiTotalPages = 0;
  /**
   * Used to tear down long-lived subscriptions (currently the Transloco
   * langChanges$ stream) when the component is destroyed.
   */
  private readonly destroy$ = new Subject<void>();

  private store = inject(Store);
  private complaintsService = inject(ComplaintsService);
  private casesService = inject(CasesService);
  private locationsService = inject(LocationsService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private translocoService = inject(TranslocoService);

  scopeOptions: { value: string; label: string }[] = [
    { value: '', label: this.translocoService.translate('all') },
    { value: 'TODAY', label: this.translocoService.translate('today') },
    { value: 'THIS_WEEK', label: this.translocoService.translate('thisWeek') },
    { value: 'ALL_TIME', label: this.translocoService.translate('allTime') },
  ];
  acknowledgmentOptions: { value: '' | 'true' | 'false'; label: string }[] = [
    { value: '', label: this.translocoService.translate('all') },
    {
      value: 'false',
      label: this.translocoService.translate('pendingComplaints'),
    },
    {
      value: 'true',
      label: this.translocoService.translate('dismissedComplaints'),
    },
  ];

  constructor(
    private fb: FormBuilder,
    @Inject(DOCUMENT) private document: File,
  ) {
    // ─── closeComplaintForm initialization - DISABLED ────────────────────────
    // This form backed the "Reject Complaint" textarea. It is no longer used
    // because the Dismiss Complaint trigger was removed from the template.
    // Kept here so the form can be restored in one step if the workflow returns.
    //
    // this.closeComplaintForm = this.fb.group({
    //   comment: ['', [Validators.required, Validators.minLength(30), Validators.maxLength(3000)]],
    // });
  }

  ngOnInit(): void {
    this.restoreUrgentCasesViewState();

    this.store.select(selectAuthUser).subscribe((user) => {
      if (!user) {
        return;
      }

      const nextLocationContextKey = this.buildLocationContextKey(user);
      if (nextLocationContextKey === this.locationContextKey) {
        this.user = user;
        return;
      }

      this.locationContextKey = nextLocationContextKey;
      this.user = user;
      this.userTrackingLevel = this.normalizeTrackingLevel(user.trackingLevel);
      this.userAssignedLocations = user.assignedLocations ?? [];
      this.initializeLocationOptionsForUser();
    });
    this.loadComplaints();
    this.loadUrgentCases(
      this.urgentCurrentPage,
      this.urgentPageSize,
      this.getUrgentCasesFilterOptions(),
    );

    // ─── Reload data on language change ───────────────────────────────────
    // The `daysAgo` pipe is pure, so its cached output won't refresh when the
    // user switches the active language. Reloading the lists (and rebuilding
    // the static option labels) forces Angular to re-run the pipe with the
    // newly translated strings. Using `skip(1)` avoids a duplicate initial
    // load because langChanges$ emits the current language synchronously on
    // subscribe, on top of the loads we just kicked off above.
    this.translocoService.langChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Rebuild static option labels that contain translated text (e.g. "All").
        this.refreshTranslatedOptionLabels();

        // Re-fetch list data so each row's `createdAt` reference changes,
        // which in turn invalidates the pure `daysAgo` pipe's cache.
        this.complaintsApiTotalPages = 0;
        this.urgentApiTotalPages = 0;
        this.loadComplaints();
        this.loadUrgentCases(
          this.urgentCurrentPage,
          this.urgentPageSize,
          this.getUrgentCasesFilterOptions(),
        );
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Rebuilds the option arrays whose labels are produced via TranslocoService.
   * Called on language change so the dropdowns reflect the new locale.
   */
  private refreshTranslatedOptionLabels(): void {
    this.scopeOptions = [
      { value: '', label: this.translocoService.translate('all') },
      { value: 'TODAY', label: this.translocoService.translate('today') },
      {
        value: 'THIS_WEEK',
        label: this.translocoService.translate('thisWeek'),
      },
      { value: 'ALL_TIME', label: this.translocoService.translate('allTime') },
    ];
    this.acknowledgmentOptions = [
      { value: '', label: this.translocoService.translate('all') },
      {
        value: 'false',
        label: this.translocoService.translate('pendingComplaints'),
      },
      {
        value: 'true',
        label: this.translocoService.translate('dismissedComplaints'),
      },
    ];
  }

  get shouldShowDistrictLocationFilter(): boolean {
    return !['SECTOR', 'CELL', 'VILLAGE'].includes(this.userTrackingLevel);
  }

  get shouldShowSectorLocationFilter(): boolean {
    return !['CELL', 'VILLAGE'].includes(this.userTrackingLevel);
  }

  get shouldShowCellLocationFilter(): boolean {
    return this.userTrackingLevel !== 'VILLAGE';
  }

  // ─── closeCommentLength getter - DISABLED ──────────────────────────────────
  // Provided the live character count under the Reject Complaint textarea.
  // No longer referenced from the template after the Dismiss workflow was
  // removed. Restore alongside closeComplaintForm if re-enabling the feature.
  //
  // get closeCommentLength(): number {
  //   const value = this.closeComplaintForm.get('comment')?.value || '';
  //   return value.length;
  // }

  // isFilterActive(option: IFilterOption): boolean {
  //   return option.scope === null
  //     ? this.rejectedComplaints
  //     : this.selectedFilter === option.scope;
  // }

  // onFilterOptionSelect(option: IFilterOption): void {
  //   if (option.scope === null) {
  //     this.onFilterRejectedChange(!this.rejectedComplaints);
  //   } else {
  //     this.onFilterChange(option.scope);
  //   }
  //   this.showFilterDropdown = false;
  // }

  // ─── Data loading ─────────────────────────────────────────────────────────

  loadComplaints(
    page: number = this.currentPage,
    size: number = this.pageSize,
    scope: string = this.selectedFilter,
    searchValue = this.searchTerm,
    isAcknowledged: boolean | null = this.isAcknowledgedFilter,
    filterOptions: Record<string, string> = this.complaintFilterOptions,
  ): void {
    this.isLoadingComplaints = true;
    this.complaintsError = null;

    // Reverse pagination: UI page 1 = last API page (oldest items), page 2 = second-to-last, etc.
    // On first load (complaintsApiTotalPages === 0) we fetch page 0 to discover totalPages,
    // then immediately reload with the actual target page.
    const apiPage = this.complaintsApiTotalPages > 0
      ? Math.max(0, this.complaintsApiTotalPages - page)
      : 0;

    this.complaintsService
      .getPendingComplaints(
        apiPage,
        size,
        scope,
        isAcknowledged,
        searchValue,
        filterOptions,
      )
      .subscribe({
        next: (cases) => {
          const totalPages = cases.payload.page.totalPages;
          this.totalItems = cases.payload.page.totalElements;

          if (this.complaintsApiTotalPages === 0) {
            this.complaintsApiTotalPages = Math.max(1, totalPages);
            if (totalPages > 1) {
              this.loadComplaints(page, size, scope, searchValue, isAcknowledged, filterOptions);
              return;
            }
          }

          this.filteredComplaints = this.sortByCreatedAt(cases.payload.content);
          this.isLoadingComplaints = false;
        },
        error: (error) => {
          this.isLoadingComplaints = false;
          this.complaintsError = error.message ?? 'Failed to load complaints';
        },
      });
  }

  loadUrgentCases(
    page: number,
    size: number,
    filterOptions: Record<string, string> = this.urgentFilterOptions,
  ): void {
    this.isLoadingUrgentCases = true;

    const apiPage = this.urgentApiTotalPages > 0
      ? Math.max(0, this.urgentApiTotalPages - page)
      : 0;

    this.casesService.getUrgentCases(apiPage, size,  filterOptions).subscribe({
      next: (response) => {
        const totalPages = response.payload.page.totalPages;
        this.urgentTotalItems = response.payload.page.totalElements;

        if (this.urgentApiTotalPages === 0) {
          this.urgentApiTotalPages = Math.max(1, totalPages);
          if (totalPages > 1) {
            this.loadUrgentCases(page, size,  filterOptions);
            return;
          }
        }

        this.isLoadingUrgentCases = false;
        this.listOfUrgentCases = this.sortByCreatedAt(response.payload.content);
        this.urgentCurrentPage = page;
        this.urgentPageSize = size;
      },
    });
  }

  onSearchValueChange(searchValue: string): void {
    this.searchTerm = searchValue;
    if (searchValue === '') {
      return;
    }
    if (this.searchDebounceId) {
      clearTimeout(this.searchDebounceId);
    }
    this.searchDebounceId = setTimeout(() => {
      this.currentPage = 1;
      this.complaintsApiTotalPages = 0;
      this.loadComplaints(
        this.currentPage,
        this.pageSize,
        this.selectedFilter,
        searchValue,
        this.isAcknowledgedFilter,
        this.complaintFilterOptions,
      );
    }, 500);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadComplaints(
      page,
      this.pageSize,
      this.selectedFilter,
      this.searchTerm,
      this.isAcknowledgedFilter,
      this.complaintFilterOptions,
    );
  }

  showFilterModal(): void {
    this.complaintFilterForm = { ...this.appliedComplaintFilterForm };
    this.isFilterModalVisible = true;
    this.loadComplaintFilterLocationOptionsForSelectedLevel();
  }

  handleFilterModalCancel(): void {
    this.isFilterModalVisible = false;
  }

  handleFilterChange(
    type: 'district' | 'sector' | 'cell' | 'village',
    value: string,
  ): void {
    if (type === 'district') {
      this.complaintFilterForm.district = value || '';
      this.complaintFilterForm.sector = '';
      this.complaintFilterForm.cell = '';
      this.complaintFilterForm.village = '';
      this.loadSectors(this.complaintFilterForm.district);
      this.loadCells('');
      this.loadVillages('');
      return;
    }

    if (type === 'sector') {
      this.complaintFilterForm.sector = value || '';
      this.complaintFilterForm.cell = '';
      this.complaintFilterForm.village = '';
      this.loadCells(this.complaintFilterForm.sector);
      this.loadVillages('');
      return;
    }

    if (type === 'cell') {
      this.complaintFilterForm.cell = value || '';
      this.complaintFilterForm.village = '';
      this.loadVillages(this.complaintFilterForm.cell);
      return;
    }

    this.complaintFilterForm.village = value || '';
  }

  handleFilterClear(): void {
    this.complaintFilterForm = this.createDefaultFilterForm();
    this.initializeLocationOptionsForUser();
    this.loadComplaintFilterLocationOptionsForSelectedLevel();
  }

  handleFilterApply(): void {
    this.appliedComplaintFilterForm = { ...this.complaintFilterForm };
    this.selectedFilter = this.appliedComplaintFilterForm.scope;
    this.isAcknowledgedFilter = this.getIsAcknowledgedValue(
      this.appliedComplaintFilterForm.isAcknowledged,
    );
    this.complaintFilterOptions = this.buildLocationFilterOptions(
      this.appliedComplaintFilterForm,
    );
    this.currentPage = 1;
    this.complaintsApiTotalPages = 0;
    this.isFilterModalVisible = false;
    this.loadComplaints(
      this.currentPage,
      this.pageSize,
      this.selectedFilter,
      this.searchTerm,
      this.isAcknowledgedFilter,
      this.complaintFilterOptions,
    );
  }

  showUrgentFilterModal(): void {
    this.urgentFilterForm = { ...this.appliedUrgentFilterForm };
    this.isUrgentFilterModalVisible = true;
    this.loadUrgentFilterLocationOptionsForSelectedLevel();
  }

  handleUrgentFilterModalCancel(): void {
    this.isUrgentFilterModalVisible = false;
  }

  handleUrgentFilterChange(
    type: 'district' | 'sector' | 'cell' | 'village',
    value: string,
  ): void {
    if (type === 'district') {
      this.urgentFilterForm.district = value || '';
      this.urgentFilterForm.sector = '';
      this.urgentFilterForm.cell = '';
      this.urgentFilterForm.village = '';
      this.loadUrgentSectors(this.urgentFilterForm.district);
      this.loadUrgentCells('');
      this.loadUrgentVillages('');
      return;
    }

    if (type === 'sector') {
      this.urgentFilterForm.sector = value || '';
      this.urgentFilterForm.cell = '';
      this.urgentFilterForm.village = '';
      this.loadUrgentCells(this.urgentFilterForm.sector);
      this.loadUrgentVillages('');
      return;
    }

    if (type === 'cell') {
      this.urgentFilterForm.cell = value || '';
      this.urgentFilterForm.village = '';
      this.loadUrgentVillages(this.urgentFilterForm.cell);
      return;
    }

    this.urgentFilterForm.village = value || '';
  }

  handleUrgentFilterClear(): void {
    this.urgentFilterForm = this.createDefaultLocationFilterForm();
    this.initializeLocationOptionsForUser();
    this.loadUrgentFilterLocationOptionsForSelectedLevel();
  }

  handleUrgentFilterApply(): void {
    this.appliedUrgentFilterForm = { ...this.urgentFilterForm };
    this.urgentFilterOptions = this.buildCaseLocationFilterOptions(
      this.appliedUrgentFilterForm,
    );
    this.hasAppliedUrgentLocationFilter = this.hasLocationFilterValues(
      this.appliedUrgentFilterForm,
    );
    this.urgentCurrentPage = 1;
    this.urgentApiTotalPages = 0;
    this.persistUrgentCasesViewState();
    this.isUrgentFilterModalVisible = false;
    this.loadUrgentCases(
      this.urgentCurrentPage,
      this.urgentPageSize,
      this.getUrgentCasesFilterOptions(),
    );
  }

  onUrgentCaseClick(id: string): void {
    this.router.navigate(['/dashboard/cases/', id]);
  }

  onComplaintClick(id: string): void {
    this.isLoading = true;
    this.complaintsService.getComplaint(id).subscribe({
      next: (response) => {
        this.selectedComplaintId = id;
        this.selectedComplaint = response;
        this.isModalVisible = true;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching complaint details:', error);
        this.isLoading = false;
      },
    });
  }

  onUrgentPageChange(page: number): void {
    this.urgentCurrentPage = page;
    this.persistUrgentCasesViewState();
    this.loadUrgentCases(
      page,
      this.urgentPageSize,
      this.getUrgentCasesFilterOptions(),
    );
  }

  onModalOkAction(): void {
    this.newCaseForm.onSubmit();
  }

  onModalCancelAction(): void {
    this.newCaseForm?.newCaseForm?.reset();
    if (this.newCaseForm?.zodErrors) this.newCaseForm.zodErrors = {};
    this.isModalVisible = false;
    this.selectedComplaint = null;
    this.selectedComplaintId = null;
  }

  onCaseCreated(response: unknown): void {
    console.log('Case created successfully:', response);
    this.isModalVisible = false;
    this.selectedComplaint = null;
    this.complaintsApiTotalPages = 0;
    this.loadComplaints();
  }

  // ─── Close / Reject Complaint handlers - DISABLED ─────────────────────────
  // These three methods drove the (now removed) Dismiss Complaint flow:
  //   * showCloseComplaintModal()    - opened the rejection modal
  //   * handleCloseComplaintSubmit() - posted the rejection comment to the API
  //   * onCloseComplaintModalCancel()- closed the rejection modal
  // They are commented out (not deleted) so the feature can be reinstated by
  // simply uncommenting these methods along with the related template block,
  // state fields, getter, and form initialization.
  //
  // showCloseComplaintModal(): void {
  //   this.isCloseComplaintModalVisible = true;
  // }
  //
  // handleCloseComplaintSubmit(): void {
  //   if (!this.selectedComplaintId) return;
  //
  //   this.isClosingComplaint = true;
  //
  //   this.complaintsService
  //     .closeComplaint(this.selectedComplaintId, this.closeComplaintForm.value.comment)
  //     .subscribe({
  //       next: (response) => {
  //         this.loadComplaints();
  //         this.isClosingComplaint = false;
  //         this.isCloseComplaintModalVisible = false;
  //         this.selectedComplaintId = null;
  //         this.selectedComplaint = null;
  //         this.closeComplaintForm.reset();
  //         this.isModalVisible = false;
  //         this.toastService.show(response.message, 'success');
  //       },
  //       error: (error) => {
  //         this.isClosingComplaint = false;
  //         this.toastService.show(
  //           error.error?.message ?? error.message ?? 'Failed to close complaint',
  //           'error'
  //         );
  //       },
  //     });
  // }
  //
  // onCloseComplaintModalCancel(): void {
  //   this.isCloseComplaintModalVisible = false;
  // }

  onClearSearch(): void {
    this.searchTerm = '';
    this.currentPage = 1;
    this.complaintsApiTotalPages = 0;
    this.loadComplaints(
      this.currentPage,
      this.pageSize,
      this.selectedFilter,
      this.searchTerm,
      this.isAcknowledgedFilter,
      this.complaintFilterOptions,
    );
  }

  private createDefaultFilterForm(): IComplaintFilterForm {
    return {
      scope: '',
      isAcknowledged: 'false',
      ...this.createDefaultLocationFilterForm(),
    };
  }

  private getIsAcknowledgedValue(value: '' | 'true' | 'false'): boolean | null {
    if (value === '') {
      return null;
    }
    return value === 'true';
  }

  private createDefaultLocationFilterForm(): ILocationFilterForm {
    return {
      district: '',
      sector: '',
      cell: '',
      village: '',
    };
  }

  private resetComplaintLocationOptions(): void {
    const allOption = {
      value: '',
      label: this.translocoService.translate('all'),
    };
    this.sectorOptions = [allOption];
    this.cellOptions = [allOption];
    this.villageOptions = [allOption];
  }

  private resetUrgentLocationOptions(): void {
    const allOption = {
      value: '',
      label: this.translocoService.translate('all'),
    };
    this.urgentSectorOptions = [allOption];
    this.urgentCellOptions = [allOption];
    this.urgentVillageOptions = [allOption];
  }

  private initializeLocationOptionsForUser(): void {
    this.complaintFilterForm = this.normalizeFormByTrackingLevel(
      this.complaintFilterForm,
    );
    this.appliedComplaintFilterForm = this.normalizeFormByTrackingLevel(
      this.appliedComplaintFilterForm,
    );
    this.urgentFilterForm = this.normalizeFormByTrackingLevel(
      this.urgentFilterForm,
    );
    this.appliedUrgentFilterForm = this.normalizeFormByTrackingLevel(
      this.appliedUrgentFilterForm,
    );
    this.urgentFilterOptions = this.buildCaseLocationFilterOptions(
      this.appliedUrgentFilterForm,
    );
    this.hasAppliedUrgentLocationFilter =
      this.hasAppliedUrgentLocationFilter &&
      this.hasLocationFilterValues(this.appliedUrgentFilterForm);

    this.resetComplaintLocationOptions();
    this.resetUrgentLocationOptions();

    const districtOptions = this.getAssignedLocationOptions('DISTRICT');
    const sectorOptions = this.getAssignedLocationOptions('SECTOR');
    const cellOptions = this.getAssignedLocationOptions('CELL');
    const villageOptions = this.getAssignedLocationOptions('VILLAGE');

    if (this.userTrackingLevel === 'VILLAGE' && villageOptions.length) {
      this.districtOptions = [];
      this.urgentDistrictOptions = [];
      this.sectorOptions = [];
      this.urgentSectorOptions = [];
      this.cellOptions = [];
      this.urgentCellOptions = [];
      const options = this.withAllOption(villageOptions);
      this.villageOptions = options;
      this.urgentVillageOptions = [...options];
      return;
    }

    if (this.userTrackingLevel === 'CELL' && cellOptions.length) {
      this.districtOptions = [];
      this.urgentDistrictOptions = [];
      this.sectorOptions = [];
      this.urgentSectorOptions = [];
      const options = this.withAllOption(cellOptions);
      this.cellOptions = options;
      this.urgentCellOptions = [...options];
      return;
    }

    if (this.userTrackingLevel === 'SECTOR' && sectorOptions.length) {
      this.districtOptions = [];
      this.urgentDistrictOptions = [];
      const options = this.withAllOption(sectorOptions);
      this.sectorOptions = options;
      this.urgentSectorOptions = [...options];
      return;
    }

    if (this.userTrackingLevel === 'DISTRICT' && districtOptions.length) {
      const options = this.withAllOption(districtOptions);
      this.districtOptions = options;
      this.urgentDistrictOptions = [...options];
      return;
    }

    if (this.userTrackingLevel === 'PROVINCE') {
      const provinceIds = this.getAssignedLocationIds('PROVINCE');
      if (provinceIds.length) {
        this.loadDistrictsByProvinces(provinceIds);
        return;
      }
    }

    this.loadDistricts();
  }

  private loadComplaintFilterLocationOptionsForSelectedLevel(): void {
    if (this.shouldShowDistrictLocationFilter) {
      this.loadSectors(this.complaintFilterForm.district);
    }
    if (this.shouldShowSectorLocationFilter) {
      this.loadCells(this.complaintFilterForm.sector);
    }
    if (this.shouldShowCellLocationFilter) {
      this.loadVillages(this.complaintFilterForm.cell);
    }
  }

  private loadUrgentFilterLocationOptionsForSelectedLevel(): void {
    if (this.shouldShowDistrictLocationFilter) {
      this.loadUrgentSectors(this.urgentFilterForm.district);
    }
    if (this.shouldShowSectorLocationFilter) {
      this.loadUrgentCells(this.urgentFilterForm.sector);
    }
    if (this.shouldShowCellLocationFilter) {
      this.loadUrgentVillages(this.urgentFilterForm.cell);
    }
  }

  private buildLocationFilterOptions(
    form: ILocationFilterForm,
  ): Record<string, string> {
    const options: Record<string, string> = {};

    if (form.district) {
      options['districtId'] = form.district;
    }
    if (form.sector) {
      options['sectorId'] = form.sector;
    }
    if (form.cell) {
      options['cellId'] = form.cell;
    }
    if (form.village) {
      options['villageId'] = form.village;
    }

    return options;
  }

  private buildCaseLocationFilterOptions(
    form: ILocationFilterForm,
  ): Record<string, string> {
    const options: Record<string, string> = {};

    if (form.district) {
      options['district'] = form.district;
    }
    if (form.sector) {
      options['sector'] = form.sector;
    }
    if (form.cell) {
      options['cell'] = form.cell;
    }
    if (form.village) {
      options['village'] = form.village;
    }

    return options;
  }

  private loadDistricts(): void {
    this.locationsService.getDistricts().subscribe({
      next: (response) => {
        const options = [
          { value: '', label: this.translocoService.translate('all') },
          ...response.payload.map((district: IContent) => ({
            value: district.id,
            label: district.name,
          })),
        ];
        this.districtOptions = options;
        this.urgentDistrictOptions = [...options];
      },
    });
  }

  private loadDistrictsByProvinces(provinceIds: string[]): void {
    const uniqueProvinceIds = Array.from(new Set(provinceIds.filter(Boolean)));
    if (!uniqueProvinceIds.length) {
      this.loadDistricts();
      return;
    }

    forkJoin(
      uniqueProvinceIds.map((provinceId) =>
        this.locationsService.getDistrictsByProvince(provinceId),
      ),
    ).subscribe({
      next: (responses) => {
        const districts = responses.flatMap(
          (response) => response.payload.content,
        );
        const options = this.withAllOption(this.toLocationOptions(districts));
        this.districtOptions = options;
        this.urgentDistrictOptions = [...options];
      },
      error: () => {
        this.loadDistricts();
      },
    });
  }

  private loadSectors(districtId: string): void {
    if (!districtId) {
      this.sectorOptions = [
        { value: '', label: this.translocoService.translate('all') },
      ];
      return;
    }

    this.locationsService.getSectors(districtId).subscribe({
      next: (response) => {
        this.sectorOptions = [
          { value: '', label: this.translocoService.translate('all') },
          ...response.payload.content.map((sector: IContent) => ({
            value: sector.id,
            label: sector.name,
          })),
        ];
      },
    });
  }

  private loadCells(sectorId: string): void {
    if (!sectorId) {
      this.cellOptions = [
        { value: '', label: this.translocoService.translate('all') },
      ];
      return;
    }

    this.locationsService.getCells(sectorId).subscribe({
      next: (response) => {
        this.cellOptions = [
          { value: '', label: this.translocoService.translate('all') },
          ...response.payload.content.map((cell: IContent) => ({
            value: cell.id,
            label: cell.name,
          })),
        ];
      },
    });
  }

  private loadVillages(cellId: string): void {
    if (!cellId) {
      this.villageOptions = [
        { value: '', label: this.translocoService.translate('all') },
      ];
      return;
    }

    this.locationsService.getVillages(cellId).subscribe({
      next: (response) => {
        this.villageOptions = [
          { value: '', label: this.translocoService.translate('all') },
          ...response.payload.content.map((village: IContent) => ({
            value: village.id,
            label: village.name,
          })),
        ];
      },
    });
  }

  private loadUrgentSectors(districtId: string): void {
    if (!districtId) {
      this.urgentSectorOptions = [
        { value: '', label: this.translocoService.translate('all') },
      ];
      return;
    }

    this.locationsService.getSectors(districtId).subscribe({
      next: (response) => {
        this.urgentSectorOptions = [
          { value: '', label: this.translocoService.translate('all') },
          ...response.payload.content.map((sector: IContent) => ({
            value: sector.id,
            label: sector.name,
          })),
        ];
      },
    });
  }

  private loadUrgentCells(sectorId: string): void {
    if (!sectorId) {
      this.urgentCellOptions = [
        { value: '', label: this.translocoService.translate('all') },
      ];
      return;
    }

    this.locationsService.getCells(sectorId).subscribe({
      next: (response) => {
        this.urgentCellOptions = [
          { value: '', label: this.translocoService.translate('all') },
          ...response.payload.content.map((cell: IContent) => ({
            value: cell.id,
            label: cell.name,
          })),
        ];
      },
    });
  }

  private loadUrgentVillages(cellId: string): void {
    if (!cellId) {
      this.urgentVillageOptions = [
        { value: '', label: this.translocoService.translate('all') },
      ];
      return;
    }

    this.locationsService.getVillages(cellId).subscribe({
      next: (response) => {
        this.urgentVillageOptions = [
          { value: '', label: this.translocoService.translate('all') },
          ...response.payload.content.map((village: IContent) => ({
            value: village.id,
            label: village.name,
          })),
        ];
      },
    });
  }

  private persistUrgentCasesViewState(): void {
    const storage = this.getSessionStorage();
    if (!storage) {
      return;
    }

    const state: IUrgentCasesViewState = {
      appliedFilterForm: this.appliedUrgentFilterForm,
      currentPage: this.urgentCurrentPage,
      pageSize: this.urgentPageSize,
    };

    try {
      storage.setItem(this.urgentCasesViewStateKey, JSON.stringify(state));
    } catch {
      // Ignore storage failures to keep the page functional.
    }
  }

  private restoreUrgentCasesViewState(): void {
    const storage = this.getSessionStorage();
    if (!storage) {
      return;
    }

    const rawState = storage.getItem(this.urgentCasesViewStateKey);
    if (!rawState) {
      return;
    }

    try {
      const parsedState = JSON.parse(
        rawState,
      ) as Partial<IUrgentCasesViewState>;
      this.appliedUrgentFilterForm = this.normalizeLocationFilterForm(
        parsedState.appliedFilterForm,
      );
      this.urgentFilterForm = { ...this.appliedUrgentFilterForm };
      this.urgentFilterOptions = this.buildCaseLocationFilterOptions(
        this.appliedUrgentFilterForm,
      );
      // A persisted filter should not automatically apply on a fresh reload.
      this.hasAppliedUrgentLocationFilter = false;

      const currentPage = Number(parsedState.currentPage);
      if (Number.isInteger(currentPage) && currentPage > 0) {
        this.urgentCurrentPage = currentPage;
      }

      const pageSize = Number(parsedState.pageSize);
      if (Number.isInteger(pageSize) && pageSize > 0) {
        this.urgentPageSize = pageSize;
      }
    } catch {
      storage.removeItem(this.urgentCasesViewStateKey);
    }
  }

  private normalizeLocationFilterForm(
    form?: Partial<ILocationFilterForm>,
  ): ILocationFilterForm {
    return {
      district: form?.district ?? '',
      sector: form?.sector ?? '',
      cell: form?.cell ?? '',
      village: form?.village ?? '',
    };
  }

  private normalizeTrackingLevel(level?: string): TrackingLevel | '' {
    const normalizedLevel = (level ?? '').toUpperCase();
    if (
      normalizedLevel === 'NATIONAL' ||
      normalizedLevel === 'PROVINCE' ||
      normalizedLevel === 'DISTRICT' ||
      normalizedLevel === 'SECTOR' ||
      normalizedLevel === 'CELL' ||
      normalizedLevel === 'VILLAGE'
    ) {
      return normalizedLevel;
    }
    return '';
  }

  private normalizeFormByTrackingLevel<T extends ILocationFilterForm>(
    form: T,
  ): T {
    const normalizedForm = { ...form };

    if (['SECTOR', 'CELL', 'VILLAGE'].includes(this.userTrackingLevel)) {
      normalizedForm.district = '';
    }
    if (['CELL', 'VILLAGE'].includes(this.userTrackingLevel)) {
      normalizedForm.sector = '';
    }
    if (this.userTrackingLevel === 'VILLAGE') {
      normalizedForm.cell = '';
    }

    return normalizedForm;
  }

  private getAssignedLocationIds(type: TrackingLevel): string[] {
    return (this.userAssignedLocations ?? [])
      .filter((location) => (location.type ?? '').toUpperCase() === type)
      .map((location) => location.id);
  }

  private getAssignedLocationOptions(
    type: TrackingLevel,
  ): { value: string; label: string }[] {
    return this.toUniqueSortedOptions(
      (this.userAssignedLocations ?? [])
        .filter((location) => (location.type ?? '').toUpperCase() === type)
        .map((location) => ({
          value: location.id,
          label: location.name,
        })),
    );
  }

  private toLocationOptions(
    items: IContent[],
  ): { value: string; label: string }[] {
    return this.toUniqueSortedOptions(
      items.map((item) => ({
        value: item.id,
        label: item.name,
      })),
    );
  }

  private toUniqueSortedOptions(
    options: { value: string; label: string }[],
  ): { value: string; label: string }[] {
    const uniqueOptions = new Map<string, string>();
    options.forEach((option) => {
      if (!uniqueOptions.has(option.value)) {
        uniqueOptions.set(option.value, option.label);
      }
    });

    return Array.from(uniqueOptions.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((first, second) => first.label.localeCompare(second.label));
  }

  private withAllOption(
    options: { value: string; label: string }[],
  ): { value: string; label: string }[] {
    return [
      { value: '', label: this.translocoService.translate('all') },
      ...options,
    ];
  }

  private hasLocationFilterValues(form: ILocationFilterForm): boolean {
    return !!(form.district || form.sector || form.cell || form.village);
  }

  private getUrgentCasesFilterOptions(): Record<string, string> {
    if (!this.hasAppliedUrgentLocationFilter) {
      return {};
    }
    return this.urgentFilterOptions;
  }

  private buildLocationContextKey(user: User): string {
    const trackingLevel = this.normalizeTrackingLevel(user.trackingLevel);
    const assignedLocationsKey = (user.assignedLocations ?? [])
      .map(
        (location) => `${(location.type ?? '').toUpperCase()}:${location.id}`,
      )
      .sort()
      .join('|');

    return `${trackingLevel}::${assignedLocationsKey}`;
  }

  private getSessionStorage(): Storage | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return window.sessionStorage;
  }

  private sortByCreatedAt<T extends { createdAt: string | Date }>(items: T[]): T[] {
    return [...items].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }
}
